import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Eye, EyeOff, RefreshCw, AlertCircle, Info, Car, CreditCard, Users, Zap, Link2 as LinkIcon } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { IntegrationAccordion } from '@/components/ui/integration-accordion'

// Setting keys
const LIMO_ANYWHERE_SETTINGS = {
  ENABLED: 'limo_anywhere_enabled',
  SYNC_BOOKINGS: 'limo_anywhere_sync_bookings',
  COMPANY: 'limo_anywhere_company',
  USERNAME: 'limo_anywhere_username',
  PASSWORD: 'limo_anywhere_password',
  CREATE_AS: 'limo_anywhere_create_as',
  SERVICE_POINT_TO_POINT: 'limo_anywhere_service_point_to_point',
  SERVICE_HOURLY: 'limo_anywhere_service_hourly',
} as const

const GO_HIGH_LEVEL_SETTINGS = {
  ENABLED: 'go_high_level_enabled',
  API_KEY: 'go_high_level_api_key',
  LOCATION_ID: 'go_high_level_location_id',
  PIPELINE_ID: 'go_high_level_pipeline_id',
  STAGE_QUOTED: 'go_high_level_stage_quoted',
  STAGE_BOOKED: 'go_high_level_stage_booked',
  // Quote-only vehicle (Get Quote) settings
  STAGE_QUOTE_REQUESTED: 'go_high_level_stage_quote_requested',
  OPERATOR_NOTIFY_PHONE: 'operator_quote_notify_phone',
  OPERATOR_NOTIFY_EMAIL: 'operator_quote_notify_email',
  OPERATOR_PRIVATE_NOTE: 'operator_quote_private_note_enabled',
  OPERATOR_AUTO_SEND_LINK: 'operator_quote_auto_send_link',
  // Custom tag settings
  TAG_BASE: 'go_high_level_tag_base',
  TAG_QUOTE_REQUEST: 'go_high_level_tag_quote_request',
  TAG_CONTINUE_PAYMENT: 'go_high_level_tag_continue_payment',
  TAG_SHARE_QUOTE: 'go_high_level_tag_share_quote',
  TAG_SELECT_VEHICLE: 'go_high_level_tag_select_vehicle',
  TAG_GET_CUSTOM_QUOTE: 'go_high_level_tag_get_custom_quote',
  // Tracking scripts injected into the viewer before </body>
  EXTERNAL_TRACKING_SCRIPT: 'go_high_level_external_tracking_script',
  DYNAMIC_CALL_TRACKING_SCRIPT: 'go_high_level_dynamic_call_tracking_script',
  TRACKING_DEBUG: 'go_high_level_tracking_debug',
} as const

const ZAPIER_SETTINGS = {
  ENABLED: 'zapier_enabled',
  WEBHOOK_URL: 'zapier_webhook_url',
  FLIGHT_DETAILS_DELAY_MINUTES: 'zapier_flight_details_delay_minutes',
  LA_CONFIRMATION_ENABLED: 'zapier_la_confirmation_enabled',
  LA_AUTO_RETRY_ENABLED: 'zapier_la_auto_retry_enabled',
} as const

const STRIPE_SETTINGS = {
  ENABLED: 'stripe_enabled',
  PUBLISHABLE_KEY: 'stripe_publishable_key',
  SECRET_KEY: 'stripe_secret_key',
  WEBHOOK_SECRET: 'stripe_webhook_secret',
  MODE: 'stripe_mode',
  PAYMENT_MODE: 'stripe_payment_mode',
  CONNECT_ACCOUNT_ID: 'stripe_connect_account_id',
} as const

/**
 * How this account's payments are processed. Set by the Connect flow, not by the
 * operator: 'connect' accounts run on platform credentials plus their connected
 * account, so they never enter their own API keys.
 */
type StripePaymentMode = 'keys' | 'connect' | 'disconnected'

// Schemas
const limoAnywhereSettingsSchema = z.object({
  limo_anywhere_enabled: z.boolean().default(false),
  limo_anywhere_sync_bookings: z.boolean().default(true),
  limo_anywhere_company: z.string().optional(),
  limo_anywhere_username: z.string().optional(),
  limo_anywhere_password: z.string().optional(),
  limo_anywhere_create_as: z.enum(['quote', 'reservation']).default('reservation'),
  limo_anywhere_service_point_to_point: z.string().optional(),
  limo_anywhere_service_hourly: z.string().optional(),
})

const goHighLevelSettingsSchema = z.object({
  go_high_level_enabled: z.boolean().default(false),
  go_high_level_api_key: z.string().optional(),
  go_high_level_location_id: z.string().optional(),
  go_high_level_pipeline_id: z.string().optional(),
  go_high_level_stage_quoted: z.string().optional(),
  go_high_level_stage_booked: z.string().optional(),
  // Quote-only vehicle (Get Quote) settings
  go_high_level_stage_quote_requested: z.string().optional(),
  operator_quote_notify_phone: z.string().optional(),
  operator_quote_notify_email: z
    .string()
    .email('Must be a valid email')
    .optional()
    .or(z.literal('')),
  operator_quote_private_note_enabled: z.boolean().default(false),
  operator_quote_auto_send_link: z.boolean().default(true),
  // Custom tag settings
  go_high_level_tag_base: z.string().optional(),
  go_high_level_tag_quote_request: z.string().optional(),
  go_high_level_tag_continue_payment: z.string().optional(),
  go_high_level_tag_share_quote: z.string().optional(),
  go_high_level_tag_select_vehicle: z.string().optional(),
  go_high_level_tag_get_custom_quote: z.string().optional(),
  // Tracking scripts injected into the viewer
  go_high_level_external_tracking_script: z.string().optional(),
  go_high_level_dynamic_call_tracking_script: z.string().optional(),
  go_high_level_tracking_debug: z.boolean().default(false),
})

const zapierSettingsSchema = z.object({
  zapier_enabled: z.boolean().default(false),
  zapier_webhook_url: z.string().url().optional().or(z.literal('')),
  // Minutes to wait after payment before syncing airport bookings to Zapier,
  // so post-payment flight details can be captured in the notes.
  zapier_flight_details_delay_minutes: z.coerce.number().int().min(1).max(60).default(5),
  // LA confirmation loop: the Zap's final step reports the Limo Anywhere
  // reservation ID back to the platform, and a watchdog alerts + retries when
  // no confirmation arrives. Off by default — requires the callback step in
  // the account's Zap, otherwise every booking would look unconfirmed.
  zapier_la_confirmation_enabled: z.boolean().default(false),
  // Whether the watchdog re-sends the booking to Zapier once before marking
  // it unconfirmed. On by default (the original behavior); when off, the
  // booking is marked unconfirmed at the first 15-minute mark.
  zapier_la_auto_retry_enabled: z.boolean().default(true),
})

// Stripe schema with key format validation
const stripeSettingsSchema = z
  .object({
    stripe_enabled: z.boolean().default(false),
    stripe_mode: z.enum(['live', 'test']).default('live'),
    stripe_publishable_key: z.string().optional().or(z.literal('')),
    stripe_secret_key: z.string().optional().or(z.literal('')),
    stripe_webhook_secret: z.string().optional().or(z.literal('')),
    // Not operator-editable — carried in the form so validation can tell a
    // Connect account (which never supplies keys) from a keys account.
    stripe_payment_mode: z.enum(['keys', 'connect', 'disconnected']).default('keys'),
  })
  .refine(
    (data) => {
      // Connect accounts run on platform credentials + their connected account,
      // so they never supply their own keys in either mode.
      if (data.stripe_payment_mode === 'connect') return true
      // If enabled in live mode, require both publishable and secret key
      // In test mode, platform credentials are used — no account keys needed
      if (data.stripe_enabled && data.stripe_mode !== 'test') {
        return data.stripe_publishable_key && data.stripe_secret_key
      }
      return true
    },
    {
      message: 'Publishable Key and Secret Key are required when Stripe is enabled',
      path: ['stripe_publishable_key'],
    }
  )
  .refine(
    (data) => {
      // Validate publishable key format if provided
      if (data.stripe_publishable_key && data.stripe_publishable_key.length > 0) {
        return (
          data.stripe_publishable_key.startsWith('pk_test_') ||
          data.stripe_publishable_key.startsWith('pk_live_')
        )
      }
      return true
    },
    {
      message: 'Publishable Key must start with pk_test_ or pk_live_',
      path: ['stripe_publishable_key'],
    }
  )
  .refine(
    (data) => {
      // Validate secret key format if provided
      if (data.stripe_secret_key && data.stripe_secret_key.length > 0) {
        return (
          data.stripe_secret_key.startsWith('sk_test_') ||
          data.stripe_secret_key.startsWith('sk_live_')
        )
      }
      return true
    },
    {
      message: 'Secret Key must start with sk_test_ or sk_live_',
      path: ['stripe_secret_key'],
    }
  )
  .refine(
    (data) => {
      // Validate webhook secret format if provided
      if (data.stripe_webhook_secret && data.stripe_webhook_secret.length > 0) {
        return data.stripe_webhook_secret.startsWith('whsec_')
      }
      return true
    },
    {
      message: 'Webhook Secret must start with whsec_',
      path: ['stripe_webhook_secret'],
    }
  )

type LimoAnywhereSettingsFormData = z.infer<typeof limoAnywhereSettingsSchema>
type GoHighLevelSettingsFormData = z.infer<typeof goHighLevelSettingsSchema>
type ZapierSettingsFormData = z.infer<typeof zapierSettingsSchema>
type StripeSettingsFormData = z.infer<typeof stripeSettingsSchema>

// Vehicle Types types
interface VehicleType {
  id: string
  name: string
  code: string | null
  displayText: string
}

interface VehicleTypesState {
  vehicleTypes: VehicleType[]
  lastSyncedAt: string | null
  isLoading: boolean
  error: string | null
}

// Platform vehicle with LA mapping
interface PlatformVehicle {
  id: string
  name: string
  vehicle_type: string
  limo_anywhere_vehicle_type_id: string | null
}

// Database LA vehicle type
interface LAVehicleTypeDB {
  id: string
  la_vehicle_type_id: string
  name: string
  code: string | null
}

export function IntegrationsPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showPassword, setShowPassword] = useState(false)
  const [showGoHighLevelKey, setShowGoHighLevelKey] = useState(false)
  const [showStripeSecretKey, setShowStripeSecretKey] = useState(false)
  const [showStripeWebhookSecret, setShowStripeWebhookSecret] = useState(false)
  const [vehicleTypesState, setVehicleTypesState] = useState<VehicleTypesState>({
    vehicleTypes: [],
    lastSyncedAt: null,
    isLoading: false,
    error: null,
  })
  const [vehicleMappings, setVehicleMappings] = useState<Record<string, string | null>>({})
  const [isSavingMappings, setIsSavingMappings] = useState(false)

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch platform vehicles for bulk mapping
  const { data: platformVehicles } = useQuery({
    queryKey: ['vehicles-for-mapping', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('vehicles')
        .select('id, name, vehicle_type, limo_anywhere_vehicle_type_id')
        .eq('account_id', activeAccount.id)
        .order('name')
      if (error) throw error
      return data as PlatformVehicle[]
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch LA vehicle types from database
  const { data: laVehicleTypesDB } = useQuery({
    queryKey: ['limo-anywhere-vehicle-types-db', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []
      const { data, error } = await supabase
        .from('limo_anywhere_vehicle_types')
        .select('id, la_vehicle_type_id, name, code')
        .eq('account_id', activeAccount.id)
        .order('name')
      if (error) throw error
      return data as LAVehicleTypeDB[]
    },
    enabled: !!activeAccount?.id,
  })

  // Initialize vehicle mappings when vehicles load
  useEffect(() => {
    if (platformVehicles) {
      const initialMappings: Record<string, string | null> = {}
      platformVehicles.forEach((v) => {
        initialMappings[v.id] = v.limo_anywhere_vehicle_type_id
      })
      setVehicleMappings(initialMappings)
    }
  }, [platformVehicles])

  // Forms
  const limoAnywhereForm = useForm<LimoAnywhereSettingsFormData>({
    resolver: zodResolver(limoAnywhereSettingsSchema),
    defaultValues: {
      limo_anywhere_enabled: false,
      limo_anywhere_sync_bookings: true,
      limo_anywhere_company: '',
      limo_anywhere_username: '',
      limo_anywhere_password: '',
      limo_anywhere_create_as: 'reservation',
      limo_anywhere_service_point_to_point: '',
      limo_anywhere_service_hourly: '',
    },
  })

  const goHighLevelForm = useForm<GoHighLevelSettingsFormData>({
    resolver: zodResolver(goHighLevelSettingsSchema),
    defaultValues: {
      go_high_level_enabled: false,
      go_high_level_api_key: '',
      go_high_level_location_id: '',
      go_high_level_pipeline_id: '',
      go_high_level_stage_quoted: '',
      go_high_level_stage_booked: '',
      go_high_level_stage_quote_requested: '',
      operator_quote_notify_phone: '',
      operator_quote_notify_email: '',
      operator_quote_private_note_enabled: false,
      operator_quote_auto_send_link: true,
      // Custom tag defaults
      go_high_level_tag_base: '',
      go_high_level_tag_quote_request: '',
      go_high_level_tag_continue_payment: '',
      go_high_level_tag_share_quote: '',
      go_high_level_tag_select_vehicle: '',
      go_high_level_tag_get_custom_quote: '',
      // Tracking script defaults
      go_high_level_external_tracking_script: '',
      go_high_level_dynamic_call_tracking_script: '',
      go_high_level_tracking_debug: false,
    },
  })

  const zapierForm = useForm<ZapierSettingsFormData>({
    resolver: zodResolver(zapierSettingsSchema),
    defaultValues: {
      zapier_enabled: false,
      zapier_webhook_url: '',
      zapier_flight_details_delay_minutes: 5,
      zapier_la_confirmation_enabled: false,
      zapier_la_auto_retry_enabled: true,
    },
  })

  const stripeForm = useForm<StripeSettingsFormData>({
    resolver: zodResolver(stripeSettingsSchema),
    defaultValues: {
      stripe_enabled: false,
      stripe_mode: 'live',
      stripe_publishable_key: '',
      stripe_secret_key: '',
      stripe_webhook_secret: '',
      stripe_payment_mode: 'keys',
    },
  })

  // Update forms when settings are loaded
  useEffect(() => {
    if (settings) {
      limoAnywhereForm.reset({
        limo_anywhere_enabled: settings[LIMO_ANYWHERE_SETTINGS.ENABLED] === 'true',
        limo_anywhere_sync_bookings: settings[LIMO_ANYWHERE_SETTINGS.SYNC_BOOKINGS] !== 'false', // defaults to true
        limo_anywhere_company: settings[LIMO_ANYWHERE_SETTINGS.COMPANY] || '',
        limo_anywhere_username: settings[LIMO_ANYWHERE_SETTINGS.USERNAME] || '',
        limo_anywhere_password: settings[LIMO_ANYWHERE_SETTINGS.PASSWORD] || '',
        limo_anywhere_create_as:
          (settings[LIMO_ANYWHERE_SETTINGS.CREATE_AS] as 'quote' | 'reservation') || 'reservation',
        limo_anywhere_service_point_to_point: settings[LIMO_ANYWHERE_SETTINGS.SERVICE_POINT_TO_POINT] || '',
        limo_anywhere_service_hourly: settings[LIMO_ANYWHERE_SETTINGS.SERVICE_HOURLY] || '',
      })
      goHighLevelForm.reset({
        go_high_level_enabled: settings[GO_HIGH_LEVEL_SETTINGS.ENABLED] === 'true',
        go_high_level_api_key: settings[GO_HIGH_LEVEL_SETTINGS.API_KEY] || '',
        go_high_level_location_id: settings[GO_HIGH_LEVEL_SETTINGS.LOCATION_ID] || '',
        go_high_level_pipeline_id: settings[GO_HIGH_LEVEL_SETTINGS.PIPELINE_ID] || '',
        go_high_level_stage_quoted: settings[GO_HIGH_LEVEL_SETTINGS.STAGE_QUOTED] || '',
        go_high_level_stage_booked: settings[GO_HIGH_LEVEL_SETTINGS.STAGE_BOOKED] || '',
        go_high_level_stage_quote_requested:
          settings[GO_HIGH_LEVEL_SETTINGS.STAGE_QUOTE_REQUESTED] || '',
        operator_quote_notify_phone: settings[GO_HIGH_LEVEL_SETTINGS.OPERATOR_NOTIFY_PHONE] || '',
        operator_quote_notify_email: settings[GO_HIGH_LEVEL_SETTINGS.OPERATOR_NOTIFY_EMAIL] || '',
        operator_quote_private_note_enabled:
          settings[GO_HIGH_LEVEL_SETTINGS.OPERATOR_PRIVATE_NOTE] === 'true',
        // Opt-out: a missing row is ON, matching the edge function's default
        operator_quote_auto_send_link:
          settings[GO_HIGH_LEVEL_SETTINGS.OPERATOR_AUTO_SEND_LINK] !== 'false',
        // Custom tag settings
        go_high_level_tag_base: settings[GO_HIGH_LEVEL_SETTINGS.TAG_BASE] || '',
        go_high_level_tag_quote_request: settings[GO_HIGH_LEVEL_SETTINGS.TAG_QUOTE_REQUEST] || '',
        go_high_level_tag_continue_payment: settings[GO_HIGH_LEVEL_SETTINGS.TAG_CONTINUE_PAYMENT] || '',
        go_high_level_tag_share_quote: settings[GO_HIGH_LEVEL_SETTINGS.TAG_SHARE_QUOTE] || '',
        go_high_level_tag_select_vehicle: settings[GO_HIGH_LEVEL_SETTINGS.TAG_SELECT_VEHICLE] || '',
        go_high_level_tag_get_custom_quote: settings[GO_HIGH_LEVEL_SETTINGS.TAG_GET_CUSTOM_QUOTE] || '',
        go_high_level_external_tracking_script:
          settings[GO_HIGH_LEVEL_SETTINGS.EXTERNAL_TRACKING_SCRIPT] || '',
        go_high_level_dynamic_call_tracking_script:
          settings[GO_HIGH_LEVEL_SETTINGS.DYNAMIC_CALL_TRACKING_SCRIPT] || '',
        go_high_level_tracking_debug:
          settings[GO_HIGH_LEVEL_SETTINGS.TRACKING_DEBUG] === 'true',
      })
      zapierForm.reset({
        zapier_enabled: settings[ZAPIER_SETTINGS.ENABLED] === 'true',
        zapier_webhook_url: settings[ZAPIER_SETTINGS.WEBHOOK_URL] || '',
        zapier_flight_details_delay_minutes:
          Number(settings[ZAPIER_SETTINGS.FLIGHT_DETAILS_DELAY_MINUTES]) || 5,
        zapier_la_confirmation_enabled:
          settings[ZAPIER_SETTINGS.LA_CONFIRMATION_ENABLED] === 'true',
        // Default ON when the setting has never been saved — only an explicit
        // 'false' turns the automatic retry off.
        zapier_la_auto_retry_enabled:
          settings[ZAPIER_SETTINGS.LA_AUTO_RETRY_ENABLED] !== 'false',
      })
      stripeForm.reset({
        stripe_enabled: settings[STRIPE_SETTINGS.ENABLED] === 'true',
        stripe_mode: (settings[STRIPE_SETTINGS.MODE] as 'live' | 'test') || 'live',
        stripe_publishable_key: settings[STRIPE_SETTINGS.PUBLISHABLE_KEY] || '',
        stripe_secret_key: settings[STRIPE_SETTINGS.SECRET_KEY] || '',
        stripe_webhook_secret: settings[STRIPE_SETTINGS.WEBHOOK_SECRET] || '',
        stripe_payment_mode:
          (settings[STRIPE_SETTINGS.PAYMENT_MODE] as StripePaymentMode) || 'keys',
      })
    }
  }, [settings, limoAnywhereForm, goHighLevelForm, zapierForm, stripeForm])

  // Mutations
  const limoAnywhereMutation = useMutation({
    mutationFn: async (data: LimoAnywhereSettingsFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.ENABLED,
          value: data.limo_anywhere_enabled ? 'true' : 'false',
          description: 'Enable Limo Anywhere integration',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.SYNC_BOOKINGS,
          value: data.limo_anywhere_sync_bookings ? 'true' : 'false',
          description: 'Enable automatic booking sync to Limo Anywhere',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.COMPANY,
          value: data.limo_anywhere_company || null,
          description: 'Limo Anywhere Company ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.USERNAME,
          value: data.limo_anywhere_username || null,
          description: 'Limo Anywhere Username',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.PASSWORD,
          value: data.limo_anywhere_password || null,
          description: 'Limo Anywhere Password',
          is_secret: true,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.CREATE_AS,
          value: data.limo_anywhere_create_as || 'reservation',
          description: 'Create as quote or reservation in Limo Anywhere',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.SERVICE_POINT_TO_POINT,
          value: data.limo_anywhere_service_point_to_point || null,
          description: 'Limo Anywhere Service ID for Point to Point trips',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: LIMO_ANYWHERE_SETTINGS.SERVICE_HOURLY,
          value: data.limo_anywhere_service_hourly || null,
          description: 'Limo Anywhere Service ID for Hourly trips',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Settings saved',
        description: 'Your Limo Anywhere settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const goHighLevelMutation = useMutation({
    mutationFn: async (data: GoHighLevelSettingsFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.ENABLED,
          value: data.go_high_level_enabled ? 'true' : 'false',
          description: 'Enable GoHighLevel integration',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.API_KEY,
          value: data.go_high_level_api_key || null,
          description: 'GoHighLevel API Key',
          is_secret: true,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.LOCATION_ID,
          value: data.go_high_level_location_id || null,
          description: 'GoHighLevel Location/Sub-account ID',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.PIPELINE_ID,
          value: data.go_high_level_pipeline_id || null,
          description: 'GoHighLevel Pipeline ID for opportunities',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.STAGE_QUOTED,
          value: data.go_high_level_stage_quoted || null,
          description: 'GoHighLevel Stage ID for quoted status',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.STAGE_BOOKED,
          value: data.go_high_level_stage_booked || null,
          description: 'GoHighLevel Stage ID for booked status',
          is_secret: false,
        },
        // Quote-only vehicle (Get Quote) settings
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.STAGE_QUOTE_REQUESTED,
          value: data.go_high_level_stage_quote_requested || null,
          description: 'GoHighLevel Stage ID for quote-requested status (falls back to Quoted stage)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.OPERATOR_NOTIFY_PHONE,
          value: data.operator_quote_notify_phone || null,
          description: 'Phone number to SMS when a Get Quote request arrives',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.OPERATOR_NOTIFY_EMAIL,
          value: data.operator_quote_notify_email || null,
          description: 'Email address to notify when a Get Quote request arrives',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.OPERATOR_PRIVATE_NOTE,
          value: data.operator_quote_private_note_enabled ? 'true' : 'false',
          description: 'Show a private note field on the operator set-price page',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.OPERATOR_AUTO_SEND_LINK,
          value: data.operator_quote_auto_send_link ? 'true' : 'false',
          description: 'Automatically text/email the customer their payment link when a price is set',
          is_secret: false,
        },
        // Custom tag settings
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TAG_BASE,
          value: data.go_high_level_tag_base || null,
          description: 'Base tag applied to all GHL contacts (default: br_lead)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TAG_QUOTE_REQUEST,
          value: data.go_high_level_tag_quote_request || null,
          description: 'Tag applied when customer submits booking form (default: br_get_vehicle_quotes)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TAG_CONTINUE_PAYMENT,
          value: data.go_high_level_tag_continue_payment || null,
          description: 'Tag applied when customer continues to payment (default: br_continue_to_payment)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TAG_SHARE_QUOTE,
          value: data.go_high_level_tag_share_quote || null,
          description: 'Tag applied when customer shares a quote (default: br_send_website_quote)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TAG_SELECT_VEHICLE,
          value: data.go_high_level_tag_select_vehicle || null,
          description: 'Tag applied when customer selects a vehicle (default: br_select_vehicle)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TAG_GET_CUSTOM_QUOTE,
          value: data.go_high_level_tag_get_custom_quote || null,
          description: 'Tag applied when customer requests a quote on a quote-only vehicle (default: br_get_custom_quote)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.EXTERNAL_TRACKING_SCRIPT,
          value: data.go_high_level_external_tracking_script || null,
          description: 'GoHighLevel external tracking script (injected into the viewer before </body>)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.DYNAMIC_CALL_TRACKING_SCRIPT,
          value: data.go_high_level_dynamic_call_tracking_script || null,
          description: 'GoHighLevel dynamic call tracking script (injected into the viewer before </body>)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: GO_HIGH_LEVEL_SETTINGS.TRACKING_DEBUG,
          value: data.go_high_level_tracking_debug ? 'true' : 'false',
          description: 'Add data-debug="true" to the GHL external tracking script for console debug logging',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Settings saved',
        description: 'Your GoHighLevel settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const zapierMutation = useMutation({
    mutationFn: async (data: ZapierSettingsFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: ZAPIER_SETTINGS.ENABLED,
          value: data.zapier_enabled ? 'true' : 'false',
          description: 'Enable Zapier integration',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: ZAPIER_SETTINGS.WEBHOOK_URL,
          value: data.zapier_webhook_url || null,
          description: 'Zapier Webhook URL',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: ZAPIER_SETTINGS.FLIGHT_DETAILS_DELAY_MINUTES,
          value: String(data.zapier_flight_details_delay_minutes),
          description: 'Minutes to defer airport-booking Zapier sync so flight details can be captured',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: ZAPIER_SETTINGS.LA_CONFIRMATION_ENABLED,
          value: data.zapier_la_confirmation_enabled ? 'true' : 'false',
          description: 'Enable the Limo Anywhere reservation confirmation loop (Zap callback + watchdog alerts)',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: ZAPIER_SETTINGS.LA_AUTO_RETRY_ENABLED,
          value: data.zapier_la_auto_retry_enabled ? 'true' : 'false',
          description: 'Automatically re-send the booking to Zapier once before marking it unconfirmed',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Settings saved',
        description: 'Your Zapier settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const stripeMutation = useMutation({
    mutationFn: async (data: StripeSettingsFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      // Always save enabled and mode
      const settingsToUpsert: Array<{
        account_id: string
        key: string
        value: string | null
        description: string
        is_secret: boolean
      }> = [
        {
          account_id: activeAccount.id,
          key: STRIPE_SETTINGS.ENABLED,
          value: data.stripe_enabled ? 'true' : 'false',
          description: 'Enable Stripe payments for this account',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: STRIPE_SETTINGS.MODE,
          value: data.stripe_mode || 'live',
          description: 'Stripe mode (live or test)',
          is_secret: false,
        },
      ]

      // Only save key fields when in live mode to avoid overwriting live keys.
      // Connect accounts never have their own keys — they run on platform
      // credentials plus their connected account — so skip them entirely.
      if (data.stripe_mode !== 'test' && data.stripe_payment_mode !== 'connect') {
        settingsToUpsert.push(
          {
            account_id: activeAccount.id,
            key: STRIPE_SETTINGS.PUBLISHABLE_KEY,
            value: data.stripe_publishable_key || null,
            description: 'Stripe Publishable Key',
            is_secret: false,
          },
          {
            account_id: activeAccount.id,
            key: STRIPE_SETTINGS.SECRET_KEY,
            value: data.stripe_secret_key || null,
            description: 'Stripe Secret Key',
            is_secret: true,
          },
          {
            account_id: activeAccount.id,
            key: STRIPE_SETTINGS.WEBHOOK_SECRET,
            value: data.stripe_webhook_secret || null,
            description: 'Stripe Webhook Signing Secret',
            is_secret: true,
          }
        )
      }

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Settings saved',
        description: 'Your Stripe settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Check if Limo Anywhere credentials are configured
  const hasLimoAnywhereCredentials = Boolean(
    settings?.[LIMO_ANYWHERE_SETTINGS.COMPANY] &&
    settings?.[LIMO_ANYWHERE_SETTINGS.USERNAME] &&
    settings?.[LIMO_ANYWHERE_SETTINGS.PASSWORD]
  )

  // Fetch vehicle types from Limo Anywhere
  const fetchVehicleTypes = async () => {
    if (!activeAccount?.id) return

    setVehicleTypesState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const { data, error } = await supabase.functions.invoke('get-limo-anywhere-vehicle-types', {
        body: {
          account_id: activeAccount.id,
          includeInactive: false,
        },
      })

      if (error) throw error

      if (data.success) {
        setVehicleTypesState({
          vehicleTypes: data.vehicleTypes || [],
          lastSyncedAt: data.syncedAt,
          isLoading: false,
          error: null,
        })
        toast({
          title: 'Vehicle types updated',
          description: `Found ${data.vehicleTypes?.length || 0} vehicle types in Limo Anywhere.`,
        })
      } else {
        throw new Error(data.error || 'Failed to fetch vehicle types')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch vehicle types'
      setVehicleTypesState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      toast({
        title: 'Failed to fetch vehicle types',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Save vehicle mappings
  const saveVehicleMappings = async () => {
    if (!platformVehicles) return

    setIsSavingMappings(true)
    try {
      // Update each vehicle with its mapping
      for (const vehicle of platformVehicles) {
        const newMapping = vehicleMappings[vehicle.id]
        // Only update if mapping changed
        if (newMapping !== vehicle.limo_anywhere_vehicle_type_id) {
          const { error } = await supabase
            .from('vehicles')
            .update({ limo_anywhere_vehicle_type_id: newMapping || null })
            .eq('id', vehicle.id)
          if (error) throw error
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['vehicles-for-mapping', activeAccount?.id] })
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })

      toast({
        title: 'Mappings saved',
        description: 'Vehicle type mappings have been updated.',
      })
    } catch (error) {
      toast({
        title: 'Error saving mappings',
        description: error instanceof Error ? error.message : 'Failed to save mappings',
        variant: 'destructive',
      })
    } finally {
      setIsSavingMappings(false)
    }
  }

  // Check if mappings have changed
  const hasMappingChanges = platformVehicles?.some(
    (v) => vehicleMappings[v.id] !== v.limo_anywhere_vehicle_type_id
  )

  const isStripeEnabled = settings?.[STRIPE_SETTINGS.ENABLED] === 'true'

  // Determine if Stripe is in test mode or live mode from the stripe_mode setting
  const stripeMode = (settings?.[STRIPE_SETTINGS.MODE] as 'live' | 'test') || 'live'

  // How payments are processed for this account (set by the Connect flow)
  const stripePaymentMode =
    (settings?.[STRIPE_SETTINGS.PAYMENT_MODE] as StripePaymentMode) || 'keys'
  const stripeConnectAccountId = settings?.[STRIPE_SETTINGS.CONNECT_ACCOUNT_ID] || ''
  const isStripeConnect = stripePaymentMode === 'connect'
  const isStripeDisconnected = stripePaymentMode === 'disconnected'

  // Connect accounts are configured once they have a connected account — they
  // never supply their own keys. In test mode, platform credentials are used, so
  // again no account keys are needed for "configured".
  const isStripeConfigured = isStripeConnect
    ? Boolean(stripeConnectAccountId)
    : stripeMode === 'test'
      ? isStripeEnabled
      : Boolean(settings?.[STRIPE_SETTINGS.PUBLISHABLE_KEY] && settings?.[STRIPE_SETTINGS.SECRET_KEY])

  // Check if other integrations are configured
  const isLimoAnywhereEnabled = settings?.[LIMO_ANYWHERE_SETTINGS.ENABLED] === 'true'
  const isGoHighLevelEnabled = settings?.[GO_HIGH_LEVEL_SETTINGS.ENABLED] === 'true'
  const isGoHighLevelConfigured = Boolean(
    settings?.[GO_HIGH_LEVEL_SETTINGS.API_KEY] &&
    settings?.[GO_HIGH_LEVEL_SETTINGS.LOCATION_ID]
  )
  const isZapierEnabled = settings?.[ZAPIER_SETTINGS.ENABLED] === 'true'
  const isZapierConfigured = Boolean(settings?.[ZAPIER_SETTINGS.WEBHOOK_URL])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Stripe Payment Settings */}
      <IntegrationAccordion
        title="Stripe Payments"
        description="Accept credit card payments through Stripe"
        icon={<CreditCard className="h-5 w-5" />}
        isEnabled={isStripeEnabled}
        isConfigured={isStripeConfigured}
        defaultOpen={!isStripeConfigured}
        modeBadge={
          isStripeConfigured && (
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                stripeMode === 'live'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {stripeMode === 'live' ? 'Live' : 'Test'} Mode
            </span>
          )
        }
      >
        <Form {...stripeForm}>
          <form
            onSubmit={stripeForm.handleSubmit((data) => stripeMutation.mutate(data))}
            className="space-y-4"
          >
            <FormField
              control={stripeForm.control}
              name="stripe_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Enable Stripe</FormLabel>
                    <FormDescription>
                      When enabled, customers can pay online using credit cards
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked)
                        // Disabling Stripe resets Test Mode back to live
                        if (!checked) stripeForm.setValue('stripe_mode', 'live')
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={stripeForm.control}
              name="stripe_mode"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Test Mode</FormLabel>
                    <FormDescription>
                      {isStripeConnect
                        ? 'Run test payments against the connected account using the platform’s Stripe test credentials. No real charges will be made.'
                        : 'Use the platform’s shared Stripe test account. No real charges will be made.'}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value === 'test'}
                      onCheckedChange={(checked) => {
                        field.onChange(checked ? 'test' : 'live')
                        if (checked) {
                          // Enabling Test Mode also enables Stripe
                          stripeForm.setValue('stripe_enabled', true)
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {stripeForm.watch('stripe_mode') === 'test' && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Test Mode Active</AlertTitle>
                <AlertDescription>
                  Using platform test credentials. Transactions will not be charged.
                </AlertDescription>
              </Alert>
            )}

            {/* Connect accounts don't enter API keys — payments run on the
                platform's credentials against their connected account. */}
            {isStripeConnect && (
              <Alert>
                <LinkIcon className="h-4 w-4" />
                <AlertTitle>Connected with Stripe</AlertTitle>
                <AlertDescription className="mt-2 space-y-2 text-sm">
                  <p>
                    This account is connected through Stripe Connect, so there are no
                    API keys to enter. Payments are processed directly on the
                    connected Stripe account.
                  </p>
                  <p>
                    <span className="text-muted-foreground">Connected account: </span>
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                      {stripeConnectAccountId}
                    </code>
                  </p>
                  <p className="text-muted-foreground">
                    {stripeForm.watch('stripe_mode') === 'test'
                      ? 'Test Mode is on — payments use the platform’s Stripe test credentials, so no real charges are made.'
                      : 'Live Mode — real payments are processed on the connected account.'}
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {isStripeDisconnected && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Stripe disconnected</AlertTitle>
                <AlertDescription>
                  This account was connected through Stripe Connect but access has been
                  revoked, so online payments are unavailable. Reconnect the account to
                  start accepting payments again.
                </AlertDescription>
              </Alert>
            )}

            {stripeForm.watch('stripe_mode') !== 'test' && !isStripeConnect && (
              <>
                <FormField
                  control={stripeForm.control}
                  name="stripe_publishable_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Publishable Key</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="pk_live_..."
                          // Password managers otherwise autofill an email/password
                          // into these fields, which can be saved as a "key".
                          autoComplete="off"
                          spellCheck={false}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Your Stripe publishable key (starts with pk_live_)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={stripeForm.control}
                  name="stripe_secret_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secret Key</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showStripeSecretKey ? 'text' : 'password'}
                            placeholder="sk_live_..."
                            // 'new-password' is what actually stops managers
                            // offering a saved credential for a password input.
                            autoComplete="new-password"
                            spellCheck={false}
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowStripeSecretKey(!showStripeSecretKey)}
                          >
                            {showStripeSecretKey ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Your Stripe secret key (starts with sk_live_)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={stripeForm.control}
                  name="stripe_webhook_secret"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Webhook Secret</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showStripeWebhookSecret ? 'text' : 'password'}
                            placeholder="whsec_..."
                            autoComplete="new-password"
                            spellCheck={false}
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowStripeWebhookSecret(!showStripeWebhookSecret)}
                          >
                            {showStripeWebhookSecret ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Your Stripe webhook signing secret (starts with whsec_). Create a webhook pointing to your site&apos;s /api/webhook/stripe endpoint.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Getting Your Stripe Keys</AlertTitle>
                  <AlertDescription className="mt-2 text-sm">
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Go to the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline text-primary">Stripe Dashboard API Keys page</a></li>
                      <li>Copy your Publishable key and Secret key</li>
                      <li>For the Webhook Secret, create a new webhook endpoint in Stripe pointing to your booking site&apos;s /api/webhook/stripe URL</li>
                    </ol>
                  </AlertDescription>
                </Alert>
              </>
            )}

            <Button type="submit" disabled={stripeMutation.isPending}>
              {stripeMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </form>
        </Form>
      </IntegrationAccordion>

      {/* Limo Anywhere Settings */}
      <IntegrationAccordion
        title="Limo Anywhere"
        description="Automatically sync bookings to your Limo Anywhere dispatch system"
        icon={<Car className="h-5 w-5" />}
        isEnabled={isLimoAnywhereEnabled}
        isConfigured={hasLimoAnywhereCredentials}
      >
          <Form {...limoAnywhereForm}>
            <form
              onSubmit={limoAnywhereForm.handleSubmit((data) => limoAnywhereMutation.mutate(data))}
              className="space-y-4"
            >
              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable Integration</FormLabel>
                      <FormDescription>
                        When enabled, bookings will be automatically sent to Limo Anywhere after payment
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_sync_bookings"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Sync Bookings</FormLabel>
                      <FormDescription>
                        Automatically push bookings to Limo Anywhere after payment. Turn off if you only want to use vehicle type syncing.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your company ID"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Your Limo Anywhere company identifier (used for login)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your username"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Your Limo Anywhere account username
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Your Limo Anywhere account password
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_create_as"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Create As</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="reservation">Reservation</SelectItem>
                        <SelectItem value="quote">Quote</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Whether to create a reservation (confirmed trip) or quote (pending trip) in Limo Anywhere
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="my-4" />
              <h3 className="text-sm font-medium mb-3">Service Type Mapping</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Map your booking service types to Limo Anywhere service codes. These are used when syncing bookings via Zapier.
              </p>

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_service_point_to_point"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Point to Point Service ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., 12345"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Limo Anywhere Service ID for point-to-point and airport trips
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={limoAnywhereForm.control}
                name="limo_anywhere_service_hourly"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hourly Service ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., 12346"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Limo Anywhere Service ID for hourly charter trips
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={limoAnywhereMutation.isPending}>
                {limoAnywhereMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>

          {/* Vehicle Types Section */}
          <Separator className="my-6" />
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Car className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-medium">Vehicle Types</h3>
              </div>
              {vehicleTypesState.lastSyncedAt && !vehicleTypesState.isLoading && (
                <span className="text-xs text-muted-foreground">
                  Last synced: {new Date(vehicleTypesState.lastSyncedAt).toLocaleString()}
                </span>
              )}
            </div>

            {/* Loading State */}
            {vehicleTypesState.isLoading && (
              <div className="flex flex-col items-center justify-center p-8 border rounded-lg bg-muted/30">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground font-medium">
                  Fetching vehicle types from Limo Anywhere...
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  This may take up to 30 seconds while we log in and retrieve your vehicle list.
                </p>
              </div>
            )}

            {/* Error State */}
            {!vehicleTypesState.isLoading && vehicleTypesState.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Failed to fetch vehicle types</AlertTitle>
                <AlertDescription className="mt-2">
                  <p>{vehicleTypesState.error}</p>
                  <p className="text-xs mt-2">
                    Please verify your Company ID, Username, and Password are correct and try again.
                  </p>
                </AlertDescription>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={fetchVehicleTypes}
                >
                  <RefreshCw className="mr-2 h-3 w-3" />
                  Try Again
                </Button>
              </Alert>
            )}

            {/* No Credentials State */}
            {!vehicleTypesState.isLoading && !vehicleTypesState.error && !hasLimoAnywhereCredentials && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Configure credentials</AlertTitle>
                <AlertDescription>
                  Enter your Limo Anywhere Company ID, Username, and Password above to fetch your vehicle types.
                </AlertDescription>
              </Alert>
            )}

            {/* Empty State (has credentials but no data yet) */}
            {!vehicleTypesState.isLoading &&
              !vehicleTypesState.error &&
              hasLimoAnywhereCredentials &&
              vehicleTypesState.vehicleTypes.length === 0 && (
                <div className="flex flex-col items-center justify-center p-8 border rounded-lg border-dashed">
                  <Car className="h-10 w-10 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground font-medium mb-1">
                    No vehicle types loaded
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Click the button below to fetch your Limo Anywhere vehicle types
                  </p>
                  <Button
                    variant="outline"
                    onClick={fetchVehicleTypes}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Fetch Vehicle Types
                  </Button>
                </div>
              )}

            {/* Success State with Data */}
            {!vehicleTypesState.isLoading &&
              !vehicleTypesState.error &&
              vehicleTypesState.vehicleTypes.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchVehicleTypes}
                    >
                      <RefreshCw className="mr-2 h-3 w-3" />
                      Refresh
                    </Button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Code
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            ID
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {vehicleTypesState.vehicleTypes.map((vt) => (
                          <tr key={vt.id} className="hover:bg-muted/50">
                            <td className="px-4 py-3 text-sm font-medium">
                              {vt.name}
                            </td>
                            <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                              {vt.code || '-'}
                            </td>
                            <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                              {vt.id}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    {vehicleTypesState.vehicleTypes.length} vehicle type{vehicleTypesState.vehicleTypes.length !== 1 ? 's' : ''} found
                  </p>
                </div>
              )}

            {/* Bulk Vehicle Mapping Section */}
            {laVehicleTypesDB && laVehicleTypesDB.length > 0 && platformVehicles && platformVehicles.length > 0 && (
              <>
                <Separator className="my-6" />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium">Vehicle Type Mapping</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        Map your platform vehicles to Limo Anywhere vehicle types for Zapier integration
                      </p>
                    </div>
                    <Button
                      onClick={saveVehicleMappings}
                      disabled={isSavingMappings || !hasMappingChanges}
                      size="sm"
                    >
                      {isSavingMappings && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                      Save Mappings
                    </Button>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Platform Vehicle
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            Limo Anywhere Vehicle Type
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {platformVehicles.map((vehicle) => (
                          <tr key={vehicle.id} className="hover:bg-muted/50">
                            <td className="px-4 py-3">
                              <div>
                                <span className="text-sm font-medium">{vehicle.name}</span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({vehicle.vehicle_type})
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Select
                                value={vehicleMappings[vehicle.id] || 'none'}
                                onValueChange={(value) =>
                                  setVehicleMappings((prev) => ({
                                    ...prev,
                                    [vehicle.id]: value === 'none' ? null : value,
                                  }))
                                }
                              >
                                <SelectTrigger className="w-[250px]">
                                  <SelectValue placeholder="Select vehicle type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Not mapped</SelectItem>
                                  {laVehicleTypesDB.map((vt) => (
                                    <SelectItem key={vt.id} value={vt.id}>
                                      {vt.name} {vt.code ? `(${vt.code})` : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {hasMappingChanges && (
                    <p className="text-xs text-amber-600 text-center">
                      You have unsaved changes
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
      </IntegrationAccordion>

      {/* GoHighLevel CRM Settings */}
      <IntegrationAccordion
        title="GoHighLevel CRM"
        description="Automatically create and update opportunities in GoHighLevel when customers book"
        icon={<Users className="h-5 w-5" />}
        isEnabled={isGoHighLevelEnabled}
        isConfigured={isGoHighLevelConfigured}
      >
          <Form {...goHighLevelForm}>
            <form
              onSubmit={goHighLevelForm.handleSubmit((data) => goHighLevelMutation.mutate(data))}
              className="space-y-4"
            >
              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable Integration</FormLabel>
                      <FormDescription>
                        When enabled, booking opportunities will be automatically synced to GoHighLevel
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_api_key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Key</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showGoHighLevelKey ? 'text' : 'password'}
                          placeholder="Enter your API key"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowGoHighLevelKey(!showGoHighLevelKey)}
                        >
                          {showGoHighLevelKey ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Your GoHighLevel API key (found in Settings &gt; Business Profile &gt; API Keys)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_location_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your Location ID"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Your GoHighLevel Location/Sub-account ID (found in Settings &gt; Business Info)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_pipeline_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pipeline ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter your Pipeline ID"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      The pipeline where opportunities should be created
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_stage_quoted"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quoted Stage ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Stage ID for quotes"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Stage to use when customer receives a quote (before payment)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_stage_booked"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Booked Stage ID</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Stage ID for bookings"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Stage to move opportunity to after successful payment
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="my-4" />
              <h3 className="text-sm font-medium mb-3">Quote-Only Vehicles (Get Quote)</h3>
              <p className="text-sm text-muted-foreground mb-4">
                When a customer requests a quote on a quote-only vehicle, you are notified
                by SMS and email (sent through GoHighLevel) with a link to set the price.
              </p>

              <FormField
                control={goHighLevelForm.control}
                name="operator_quote_notify_phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+12125551234" {...field} />
                    </FormControl>
                    <FormDescription>
                      Phone number that receives an SMS with the set-price link for new quote requests
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="operator_quote_notify_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification Email</FormLabel>
                    <FormControl>
                      <Input placeholder="dispatch@yourcompany.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Email address that receives the set-price link for new quote requests
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_stage_quote_requested"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quote Requested Stage ID (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Stage ID for quote requests" {...field} />
                    </FormControl>
                    <FormDescription>
                      Stage for new Get Quote requests. Leave blank to use the Quoted stage.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="operator_quote_private_note_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Private pricing notes</FormLabel>
                      <FormDescription>
                        Show an optional private note field on the set-price page
                        (e.g. sourcing cost). Notes are never shown to the customer.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="operator_quote_auto_send_link"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Auto-send price to customer</FormLabel>
                      <FormDescription>
                        Text the customer their price and payment link (email if they have no
                        phone) every time you set or change a price. When off, you send the
                        link yourself from the set-price page.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Separator className="my-4" />
              <h3 className="text-sm font-medium mb-3">Tag Configuration (Optional)</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Customize the tags applied to contacts at different stages of the booking flow. Leave blank to use default values.
              </p>

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tag_base"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Base Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="br_lead"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Applied to all contacts (default: br_lead)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tag_quote_request"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quote Request Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="br_get_vehicle_quotes"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Applied when customer submits the booking form - Step 1 (default: br_get_vehicle_quotes)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tag_continue_payment"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Continue to Payment Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="br_continue_to_payment"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Applied when customer clicks Continue on payment page - Step 3 (default: br_continue_to_payment)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tag_share_quote"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Share Quote Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="br_send_website_quote"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Applied when customer uses the Share Quote feature (default: br_send_website_quote)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tag_select_vehicle"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Select Vehicle Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="br_select_vehicle"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Applied when customer selects a vehicle (default: br_select_vehicle)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tag_get_custom_quote"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Get Custom Quote Tag</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="br_get_custom_quote"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Applied when customer clicks Get Quote on a quote-only vehicle (default: br_get_custom_quote)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator className="my-4" />

              <div>
                <h3 className="text-sm font-medium">Tracking Scripts</h3>
                <p className="text-sm text-muted-foreground">
                  Paste the External tracking and Dynamic Call tracking snippets copied from
                  GoHighLevel. They load on your public website before the closing &lt;/body&gt; tag
                  when the integration is enabled above.
                </p>
              </div>

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_external_tracking_script"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>External Tracking Script</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={'<script src="https://link.example.com/js/external_tracking.js"></script>'}
                        className="min-h-[150px] resize-y font-mono text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      GoHighLevel external tracking snippet. Loads on every public page.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_dynamic_call_tracking_script"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dynamic Call Tracking Script</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={'<script src="https://link.example.com/js/dynamic_call_tracking.js"></script>'}
                        className="min-h-[150px] resize-y font-mono text-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      GoHighLevel dynamic call tracking (number swap) snippet.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={goHighLevelForm.control}
                name="go_high_level_tracking_debug"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable debug logging</FormLabel>
                      <FormDescription>
                        Adds data-debug="true" to the external tracking script so GHL writes
                        debug output to the browser console.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={goHighLevelMutation.isPending}>
                {goHighLevelMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>
      </IntegrationAccordion>

      {/* Zapier Settings */}
      <IntegrationAccordion
        title="Zapier"
        description="Send booking data to a Zapier webhook for custom automation workflows"
        icon={<Zap className="h-5 w-5" />}
        isEnabled={isZapierEnabled}
        isConfigured={isZapierConfigured}
      >
          <Form {...zapierForm}>
            <form
              onSubmit={zapierForm.handleSubmit((data) => zapierMutation.mutate(data))}
              className="space-y-4"
            >
              <FormField
                control={zapierForm.control}
                name="zapier_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable Integration</FormLabel>
                      <FormDescription>
                        When enabled, completed bookings will be sent to your Zapier webhook
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={zapierForm.control}
                name="zapier_webhook_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Webhook URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://hooks.zapier.com/hooks/catch/..."
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      The Zapier webhook URL that will receive booking data. Create a &quot;Webhooks by Zapier&quot; trigger to get this URL.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={zapierForm.control}
                name="zapier_la_confirmation_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">LA Reservation Confirmation</FormLabel>
                      <FormDescription>
                        Your Zap&apos;s final step reports the Limo Anywhere reservation ID back
                        to the platform. Bookings still unconfirmed after 15 minutes trigger a
                        Slack alert. Only enable after adding the callback webhook step to this
                        account&apos;s Zap — otherwise every booking will look unconfirmed and
                        alert.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={zapierForm.control}
                name="zapier_la_auto_retry_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Automatic Retry</FormLabel>
                      <FormDescription>
                        When the LA reservation isn&apos;t confirmed within 15 minutes,
                        automatically re-send the booking to Zapier once before marking it
                        unconfirmed. When off, the booking is marked unconfirmed and alerts
                        at the first 15-minute mark. Manual retry from the booking page is
                        always available. Only applies while LA Reservation Confirmation is
                        enabled.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={zapierForm.control}
                name="zapier_flight_details_delay_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Flight details wait time (minutes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={60}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      For airport bookings, wait this long after payment before sending to Zapier, giving customers time to enter flight details on the confirmation step. Non-airport bookings are sent immediately. Default: 5.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Webhook Payload</AlertTitle>
                <AlertDescription className="mt-2">
                  <p className="text-sm">
                    When a booking is completed, the following data is sent to your webhook:
                  </p>
                  <ul className="text-xs mt-2 space-y-1 list-disc list-inside text-muted-foreground">
                    <li>first_name, last_name, email, phone (E.164 format)</li>
                    <li>pick_up_datetime (ISO 8601), pick_up_address, pickup_lat, pickup_lng</li>
                    <li>drop_off_address, drop_off_lat, drop_off_lng</li>
                    <li>vehicle_code (from LA vehicle type mapping)</li>
                    <li>service_code (from service type mapping)</li>
                    <li>notes (includes booking price, plus flight details for airport trips)</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Button type="submit" disabled={zapierMutation.isPending}>
                {zapierMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>
      </IntegrationAccordion>
    </div>
  )
}
